import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { financingTypes, consultationImages } from '../data/mock';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Home, CreditCard, RefreshCw, Wallet, Briefcase, Banknote, ArrowRight, CheckCircle2, TrendingDown, Shield, Clock } from 'lucide-react';
import ChatWidget from '../components/common/ChatWidget';

const iconMap = {
  Home,
  CreditCard,
  RefreshCw,
  Wallet,
  Briefcase,
  Banknote
};

const ComparisonPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-blue-400 font-semibold text-sm uppercase tracking-wider mb-4">
                Finanzierungsvergleich
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Alle Finanzierungsarten im Überblick
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                Vergleichen Sie verschiedene Finanzierungsoptionen und finden Sie die perfekte Lösung für Ihre Bedürfnisse.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/anfrage">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                    Jetzt vergleichen
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Financing Types Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Wählen Sie Ihre Finanzierungsart
              </h2>
              <p className="text-slate-600">
                Jede Finanzierungslösung ist auf unterschiedliche Bedürfnisse zugeschnitten.
              </p>
            </div>

            <div className="space-y-8">
              {financingTypes.map((type, index) => {
                const IconComponent = iconMap[type.icon];
                const isEven = index % 2 === 0;
                
                return (
                  <div
                    key={type.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-slate-100"
                  >
                    <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                      {/* Content */}
                      <div className="flex-1 p-8 lg:p-12">
                        <div className="flex items-center gap-4 mb-6">
                          <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                            {IconComponent && <IconComponent className="w-7 h-7 text-blue-600" />}
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-slate-900">{type.title}</h3>
                            <p className="text-blue-600 font-medium">{type.shortDesc}</p>
                          </div>
                        </div>

                        <p className="text-slate-600 mb-6 leading-relaxed">
                          {type.description}
                        </p>

                        <div className="grid sm:grid-cols-2 gap-3 mb-8">
                          {type.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="text-sm text-slate-700">{feature}</span>
                            </div>
                          ))}
                        </div>

                        <Link to="/anfrage">
                          <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                            Angebot einholen
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </div>

                      {/* Stats Card */}
                      <div className="lg:w-80 bg-slate-50 p-8 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-slate-100">
                        <div className="space-y-6">
                          <div>
                            <p className="text-sm text-slate-500 mb-1">Zinssatz ab</p>
                            <p className="text-3xl font-bold text-blue-600">{type.rates.minRate}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 mb-1">Max. Betrag</p>
                            <p className="text-2xl font-bold text-slate-900">{type.rates.maxAmount}</p>
                          </div>
                          <div>
                            <p className="text-sm text-slate-500 mb-1">Laufzeit</p>
                            <p className="text-lg font-semibold text-slate-700">{type.rates.term}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Vergleichstabelle
              </h2>
              <p className="text-slate-600">
                Alle Finanzierungsarten auf einen Blick
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl overflow-hidden shadow-lg">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-6 py-4 text-left font-semibold">Finanzierungsart</th>
                    <th className="px-6 py-4 text-center font-semibold">Zinssatz ab</th>
                    <th className="px-6 py-4 text-center font-semibold">Max. Betrag</th>
                    <th className="px-6 py-4 text-center font-semibold">Laufzeit</th>
                    <th className="px-6 py-4 text-center font-semibold">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {financingTypes.map((type, index) => {
                    const IconComponent = iconMap[type.icon];
                    return (
                      <tr key={type.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                              {IconComponent && <IconComponent className="w-5 h-5 text-blue-600" />}
                            </div>
                            <div>
                              <p className="font-semibold text-slate-900">{type.title}</p>
                              <p className="text-xs text-slate-500">{type.shortDesc}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span className="text-lg font-bold text-blue-600">{type.rates.minRate}</span>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span className="font-semibold text-slate-900">{type.rates.maxAmount}</span>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <span className="text-slate-700">{type.rates.term}</span>
                        </td>
                        <td className="px-6 py-5 text-center">
                          <Link to="/anfrage">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                              Anfragen
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: TrendingDown,
                  title: 'Beste Zinsen',
                  description: 'Durch unseren Vergleich von über 500 Banken sichern Sie sich die günstigsten Konditionen.'
                },
                {
                  icon: Shield,
                  title: 'Sicher & Seriös',
                  description: 'Alle unsere Partnerbanken sind etablierte Institute mit höchsten Sicherheitsstandards.'
                },
                {
                  icon: Clock,
                  title: 'Schnelle Zusage',
                  description: 'Erhalten Sie innerhalb von 24 Stunden ein verbindliches Angebot.'
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bereit für Ihre Finanzierung?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Stellen Sie jetzt Ihre unverbindliche Anfrage und erhalten Sie die besten Angebote.
            </p>
            <Link to="/anfrage">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-lg">
                Jetzt Anfrage stellen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ComparisonPage;
